import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FormRecordDto } from '../../../../dtos/patientmr/forms.dto';
import { FormService } from '../../../../services/patientmr/forms.service';
import { AlertPill } from '../../../common/Pills';
import FlowSheetTotalsBar, { FlowSheetTotalsTarget } from '../../forms/FlowSheetTotalsBar';
import NursingAddendumForm from '../../forms/NursingAddendumForm';
import { asFlowsheetGrid, parseHours, perWeekToHours } from '../../../../utils/flowsheetTotals';

interface LinkedFlowSheetSectionProps {
  patientId: string;
  linkedFlowSheetFormId: string;
  onChange: (field: string, value: string | number | null) => void;
  readOnly?: boolean;
  /** Medicaid PDN: submit needs a finalized, complete F00120 (§3.6a) */
  required?: boolean;
  /** PA unit config, to compare the grid with the approved per-week quantity */
  approvedPerWeek?: number | null;
  unitType?: string | null;
  minutesPerUnit?: number | null;
  onError?: (message: string) => void;
  defaultOpen?: boolean;
}

/** Statuses at or after `finalize` (Phase 8 Q4). */
const FINALIZED_STATUSES = new Set(['COMPLETE', 'CURRENT']);

function formLabel(f: FormRecordDto): string {
  const data = f.data || {};
  const range = [data.requestedStartDate, data.requestedEndDate].filter(v => typeof v === 'string' && v).join(' – ');
  return `F00120 · ${f.status}${range ? ` · ${range}` : ''} · updated ${new Date(f.updatedAt).toLocaleDateString()}`;
}

function downloadBase64Pdf(pdf: string, contentType: string, filename: string): void {
  const byteCharacters = atob(pdf);
  const byteNumbers = new Array<number>(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** "Linked F00120" card (PA-09): pick the NURSING_ADDENDUM form; view status, totals, open and print. */
export const LinkedFlowSheetSection: React.FC<LinkedFlowSheetSectionProps> = ({
  patientId,
  linkedFlowSheetFormId,
  onChange,
  readOnly = false,
  required = false,
  approvedPerWeek = null,
  unitType = null,
  minutesPerUnit = null,
  onError,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [forms, setForms] = useState<FormRecordDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    FormService.list(patientId)
      .then(all => {
        if (!cancelled) setForms(all.filter(f => f.formType === 'NURSING_ADDENDUM' && !f.deleted));
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setForms([]);
          setLoadError(e instanceof Error && e.message ? e.message : 'Failed to load forms');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const selected = forms.find(f => f.formId === linkedFlowSheetFormId) || null;
  const finalized = !!selected && FINALIZED_STATUSES.has(selected.status);

  const handlePrint = async () => {
    if (!selected) return;
    setPrinting(true);
    try {
      const result = await FormService.generatePdf(patientId, selected.formId);
      downloadBase64Pdf(result.pdf, result.contentType, result.filename);
    } catch (e: unknown) {
      onError?.(e instanceof Error && e.message ? e.message : 'PDF generation failed. Please try again.');
    } finally {
      setPrinting(false);
    }
  };

  const targets: FlowSheetTotalsTarget[] = [];
  if (selected) {
    targets.push({ label: 'PDN hours requested per week on the F00120', hours: parseHours(selected.data?.pdnHoursPerWeek) });
  }
  const approvedHours = perWeekToHours(approvedPerWeek, unitType, minutesPerUnit);
  if (approvedHours !== null) targets.push({ label: 'approved per week on this PA', hours: approvedHours });

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="linked-flowsheet">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50"
        type="button"
      >
        <div className="flex flex-wrap items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Linked F00120 Flow Sheet</span>
          {required && <AlertPill type={finalized ? 'success' : 'warning'} text={finalized ? 'Ready' : 'Required to submit'} />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3 text-left">
          {required && (
            <p className="text-xs text-slate-600">
              Medicaid PDN/HHA requests need a linked F00120 that is finalized and has a caregiver code on every
              care entry and on each day of the week.
            </p>
          )}

          {!readOnly && (
            <div>
              <label htmlFor="pa-linked-f00120" className="block text-sm font-medium text-slate-700 mb-1.5">
                Select F00120 (Nursing Addendum){required && <span className="text-red-400"> *</span>}
              </label>
              <select
                id="pa-linked-f00120"
                value={linkedFlowSheetFormId || ''}
                onChange={e => onChange('linkedFlowSheetFormId', e.target.value)}
                disabled={loading}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">No linked flow sheet</option>
                {forms.map(f => <option key={f.formId} value={f.formId}>{formLabel(f)}</option>)}
                {linkedFlowSheetFormId && !selected && !loading && (
                  <option value={linkedFlowSheetFormId}>Linked form ({linkedFlowSheetFormId})</option>
                )}
              </select>
              {loading && <p className="mt-1 text-xs text-slate-500">Loading forms...</p>}
              {!loading && forms.length === 0 && !loadError && (
                <p className="mt-1 text-xs text-slate-500">No F00120 forms yet. Create one in Forms.</p>
              )}
            </div>
          )}
          {loadError && <p className="text-xs text-red-600">{loadError}</p>}

          {!linkedFlowSheetFormId && readOnly && <p className="text-sm text-slate-500">No F00120 linked.</p>}

          {selected && (
            <div className="rounded-md border border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-800">F00120</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                    finalized ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}
                  data-testid="linked-flowsheet-status"
                >
                  {selected.status}
                </span>
                {!finalized && <span className="text-xs text-amber-700">Finalize it in Forms before submitting.</span>}
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(s => !s)}
                    className="min-h-[44px] sm:min-h-0 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    {showForm ? 'Hide' : 'Open'}
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    disabled={printing}
                    className="min-h-[44px] sm:min-h-0 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {printing ? 'Generating...' : 'Print'}
                  </button>
                </div>
              </div>
              <FlowSheetTotalsBar grid={asFlowsheetGrid(selected.data?.flowsheet)} targets={targets} />
              {showForm && (
                <div className="border-t border-slate-100 pt-3">
                  <p className="mb-2 text-xs text-slate-500">Read-only. Edit the flow sheet in Forms.</p>
                  <NursingAddendumForm isReadOnly initialData={selected.data} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LinkedFlowSheetSection;
